/**
 * ErrorBoundary Component
 *
 * Catches React component errors and logs them to the database.
 * Shows a user-friendly error message instead of crashing the entire app.
 */

import { Component } from 'react';
import { logError } from '../utils/logger';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to database
    logError(
      'react_component_error',
      error,
      {
        componentStack: errorInfo.componentStack,
        errorBoundary: this.props.name || 'ErrorBoundary'
      }
    );

    // Update state with error details
    this.setState({
      error,
      errorInfo
    });

    // Also log to console for development
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });

    // Reload the page to reset state
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          padding: '20px'
        }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
            padding: '40px'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div>
              <h1 style={{ fontSize: '28px', color: '#dc2626', marginBottom: '10px', fontWeight: '700' }}>
                Oops! Something went wrong
              </h1>
              <p style={{ color: '#64748b', fontSize: '16px', lineHeight: '1.6' }}>
                We encountered an unexpected error. Don't worry - this has been logged and we'll look into it.
              </p>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div style={{
                background: '#fee2e2',
                border: '2px solid #ef4444',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <div style={{ fontWeight: '600', color: '#991b1b', marginBottom: '8px', fontSize: '14px' }}>
                  Error Details (Development Only):
                </div>
                <div style={{ fontSize: '13px', color: '#7f1d1d', fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Message:</strong> {this.state.error.toString()}
                  </div>
                  {this.state.errorInfo && (
                    <div>
                      <strong>Component Stack:</strong>
                      <div style={{ fontSize: '12px', marginTop: '4px' }}>
                        {this.state.errorInfo.componentStack}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={this.handleReset}
                style={{
                  padding: '12px 32px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              >
                Reload Page
              </button>

              <button
                onClick={() => window.history.back()}
                style={{
                  padding: '12px 32px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  background: 'white',
                  color: '#374151',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f9fafb';
                  e.target.style.borderColor = '#cbd5e1';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.borderColor = '#e5e7eb';
                }}
              >
                Go Back
              </button>
            </div>

            <div style={{
              marginTop: '30px',
              textAlign: 'center',
              fontSize: '14px',
              color: '#94a3b8'
            }}>
              If this problem persists, please contact support with the error details above.
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
